import { useState, useEffect, useCallback } from 'react'
import { Search, Loader2, Download, Shield, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'

interface PointRecord {
  id: string
  user_name: string
  user_phone: string
  event_type_name: string
  points: number
  status: string
  created_at: string
  hospital_name?: string
  remark?: string
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const AdminPointRecords = () => {
  const [records, setRecords] = useState<PointRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [submittedSearch, setSubmittedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const totalPages = Math.ceil(total / pageSize)

  // 删除弹窗状态
  const [deleteTarget, setDeleteTarget] = useState<PointRecord | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [toastVisible, setToastVisible] = useState(false)

  const showToast = (msg: string) => {
    setToastMsg(msg)
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 2000)
    setTimeout(() => setToastMsg(null), 2500)
  }

  // 搜索框下方提示
  const [searchError, setSearchError] = useState('')

  // 权限判断
  const rawAuthUser = localStorage.getItem('auth_user')
  const authUser = rawAuthUser ? JSON.parse(rawAuthUser) : null
  const isSupervisor = authUser?.role === 'medical' && authUser?.medicalRole === 'supervisor'
  const isAdmin = authUser?.role === 'admin'
  // 管理员 + 机构负责人 都可删除
  const canDelete = isAdmin || isSupervisor

  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(currentPage), page_size: String(pageSize) })
      if (submittedSearch) params.append('keyword', submittedSearch)
      const res = await fetch(`/api/v1/points/records?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const data = await res.json()
      setRecords((data.records || data.items || []))
      setTotal(data.total || 0)
    } catch (e) {
      console.error('Failed to load records', e)
    } finally {
      setLoading(false)
    }
  }, [currentPage, submittedSearch, pageSize])

  const handleSearch = () => {
    const val = searchInput.trim()
    // 清空搜索直接执行
    if (!val) {
      setSearchError('')
      setCurrentPage(1)
      setSubmittedSearch('')
      return
    }
    // 纯数字：至少 4 位
    if (/^\d+$/.test(val)) {
      if (val.length < 4) {
        setSearchError('数字搜索至少需要 4 位')
        return
      }
    } else {
      // 统计汉字数量
      const chineseCount = (val.match(/[\u4e00-\u9fff]/g) || []).length
      if (chineseCount < 2) {
        setSearchError('关键词至少包含 2 个汉字')
        return
      }
    }
    setSearchError('')
    setCurrentPage(1)
    setSubmittedSearch(val)
  }

  useEffect(() => { loadRecords() }, [loadRecords])

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token')
      const params = new URLSearchParams()
      if (submittedSearch) params.append('keyword', submittedSearch)
      const res = await fetch(`/api/v1/export/points?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('导出失败')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `积分记录_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      alert('导出失败：' + (e as Error).message)
    }
  }

  // 删除确认
  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await fetch(`/api/v1/points/records/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const data = await res.json()
      if (!res.ok) {
        alert(data.detail || '删除失败')
        return
      }
      showToast('删除成功')
      // 如果当前页只剩一条且不是第一页，退回上一页
      if (records.length === 1 && currentPage > 1) {
        setCurrentPage(p => p - 1)
      } else {
        loadRecords()
      }
      setDeleteTarget(null)
    } catch (e) {
      alert('删除失败：' + (e as Error).message)
    } finally {
      setDeleting(false)
    }
  }

  // 生成页码数组（最多显示 7 个页码按钮）
  const getPageNumbers = (): (number | string)[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages: (number | string)[] = []
    pages.push(1)
    if (currentPage > 3) pages.push('...')
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700'
      case 'pending': return 'bg-yellow-100 text-yellow-700'
      case 'cancelled': return 'bg-red-100 text-red-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed': return '已确认'
      case 'pending': return '待确认'
      case 'cancelled': return '已撤销'
      default: return status
    }
  }

  // 跳转输入
  const [jumpInput, setJumpInput] = useState('')
  const handleJump = () => {
    const p = parseInt(jumpInput)
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      setCurrentPage(p)
      setJumpInput('')
    }
  }

  return (
    <div className="space-y-6">
            {/* Toast 提示 */}
      {toastMsg && (
        <div className="fixed top-6 right-6 z-[100]" style={{
          animation: toastVisible ? 'slideIn 0.3s ease-out' : 'slideOut 0.4s ease-in forwards',
          pointerEvents: 'none'
        }}>
          <div className="bg-green-700 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{toastMsg}</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            积分记录
            {isSupervisor && (
              <span className="flex items-center gap-1 text-sm font-normal text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                <Shield size={14} /> 本机构
              </span>
            )}
          </h1>
          <p className="text-gray-500 text-sm mt-1">共 {total} 条记录</p>
        </div>
        <button onClick={handleExport}
          className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800">
          <Download size={18} /> 导出
        </button>
      </div>

      {/* 搜索 */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索用户姓名/手机号/事件类型..."
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setSearchError('') }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="bg-green-700 text-white px-6 py-3 rounded-xl hover:bg-green-800 transition-colors flex items-center gap-2"
          >
            <Search size={18} />
            <span>搜索</span>
          </button>
        </div>
        {searchError && (
          <p className="text-red-500 text-sm mt-2 ml-1">{searchError}</p>
        )}
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12 text-gray-400">暂无积分记录</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">用户信息</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">机构</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">事件类型</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">积分</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">状态</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">时间</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">备注</th>
                  {canDelete && <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">操作</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{record.user_name || '未知用户'}</p>
                      <p className="text-sm text-gray-500">{record.user_phone || '-'}</p>
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-sm">{record.hospital_name || '-'}</td>
                    <td className="px-6 py-4 text-gray-700">{record.event_type_name || '-'}</td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-green-600">+{record.points}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(record.status)}`}>
                        {getStatusLabel(record.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {record.created_at ? new Date(record.created_at).toLocaleString('zh-CN') : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm" title={record.remark || ''}>
                      {record.remark && record.remark.length > 10 ? record.remark.slice(0, 10) + '...' : (record.remark || '-')}
                    </td>
                    {canDelete && (
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => setDeleteTarget(record)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                          title="删除该记录"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 分页 */}
      {totalPages > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* 左侧：统计 */}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>共 {total} 条</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} 条/页</option>)}
            </select>
          </div>

          {/* 右侧：页码 + 跳转 */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              {/* 上一页 */}
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>

              {/* 页码按钮 */}
              {getPageNumbers().map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p as number)}
                    className={`min-w-[36px] px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === p
                        ? 'bg-green-700 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

              {/* 下一页 */}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronRight size={16} />
              </button>

              {/* 跳转 */}
              <div className="flex items-center gap-1 ml-2">
                <span className="text-sm text-gray-400">跳至</span>
                <input
                  type="text"
                  value={jumpInput}
                  onChange={(e) => setJumpInput(e.target.value.replace(/[^0-9]/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                  onBlur={handleJump}
                  placeholder={String(currentPage)}
                  className="w-12 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-green-500"
                />
                <span className="text-sm text-gray-400">页</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 删除确认弹窗 */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">确认删除积分记录</h3>
                <p className="text-sm text-gray-500 mt-1">此操作不可恢复，请谨慎操作</p>
              </div>
            </div>

            {/* 记录详情 */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm mb-5">
              <div className="flex justify-between">
                <span className="text-gray-500">用户</span>
                <span className="text-gray-800 font-medium">{deleteTarget.user_name} ({deleteTarget.user_phone})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">事件类型</span>
                <span className="text-gray-800">{deleteTarget.event_type_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">积分</span>
                <span className="text-green-600 font-bold">+{deleteTarget.points}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">状态</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(deleteTarget.status)}`}>
                  {getStatusLabel(deleteTarget.status)}
                </span>
              </div>
              {deleteTarget.status === 'confirmed' && (
                <div className="mt-2 pt-2 border-t border-gray-200 text-red-600 text-xs">
                  ⚠ 该记录为「已确认」状态，删除后将同步扣减用户 {deleteTarget.points} 积分
                </div>
              )}
            </div>

            {/* 按钮 */}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> 删除中...
                  </span>
                ) : (
                  '确认删除（不可恢复）'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPointRecords

// 注入 Toast 动画
const style = document.createElement('style')
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`
if (!document.querySelector('style[data-toast-anim]')) {
  style.setAttribute('data-toast-anim', '1')
  document.head.appendChild(style)
}
